import { useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SearchSelect } from "@/components/SearchSelect";
import { splitAddress } from "@/components/schedule/PropertyAddressLines";
import { Upload, Loader2, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE = "First line,Second line,Postcode,Access info,Notes\n269 Great Cheetham Street East,Salford,M7 4UF,Key safe 1234,\n";

// Minimal CSV parser (handles quoted fields, commas and newlines inside quotes).
function parseCSV(text) {
  const rows = []; let row = [], cur = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; } else if (c === '"') q = false; else cur += c; }
    else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cur); rows.push(row); row = []; cur = ""; }
    else cur += c;
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim()));
}

const COLS = { line1: ["first line", "address line 1", "line 1", "address1", "address"], line2: ["second line", "address line 2", "line 2", "town", "city", "address2"],
  postcode: ["postcode", "post code", "zip"], accessInfo: ["access info", "access"], notes: ["notes", "note"] };

function rowsFromCSV(text) {
  const all = parseCSV(text);
  if (!all.length) return [];
  const head = all[0].map((h) => h.trim().toLowerCase());
  const idx = Object.fromEntries(Object.entries(COLS).map(([k, names]) => [k, head.findIndex((h) => names.includes(h))]));
  const hasHeader = idx.line1 >= 0 || idx.postcode >= 0;
  const body = hasHeader ? all.slice(1) : all;
  const pick = (r, k, fallback) => ((hasHeader ? r[idx[k]] : r[fallback]) || "").trim();
  return body.map((r) => ({ line1: pick(r, "line1", 0), line2: pick(r, "line2", 1), postcode: pick(r, "postcode", 2), accessInfo: pick(r, "accessInfo", 3), notes: pick(r, "notes", 4) }));
}

const rowsFromPaste = (text) => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => ({ ...splitAddress(l), accessInfo: "", notes: "" }));

const BADGE = { ok: "text-emerald-700", created: "text-emerald-700", duplicate: "text-amber-700", error: "text-red-600" };

export function PropertyImport({ open, onOpenChange, clients, onDone }) {
  const [clientId, setClientId] = useState("");
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setText(""); setRows([]); setPreview(null); };

  const check = async (rs) => {
    if (!clientId) { toast.error("Choose the client first"); return; }
    if (!rs.length) { toast.error("No addresses found"); return; }
    setBusy(true);
    try { const { data } = await client.post("/properties/import", { clientId, rows: rs, dryRun: true }); setRows(rs); setPreview(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    f.text().then((tx) => check(rowsFromCSV(tx)));
  };
  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/properties/import", { clientId, rows, dryRun: false });
      toast.success(`${data.created} properties added${data.withoutPin ? ` · ${data.withoutPin} need a map pin` : ""}`);
      setPreview(data); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const done = preview && preview.created !== undefined;
  const template = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    a.download = "properties-template.csv"; a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="property-import-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import properties</DialogTitle>
          <DialogDescription>Add many properties to one client from a CSV file or a pasted list. Each address gets a map pin automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Client</Label>
            <SearchSelect testid="import-client-select" value={clientId} onChange={(v) => { setClientId(v); setPreview(null); }}
              options={clients.map((c) => ({ value: c.id, label: c.name }))} placeholder="Choose the client" className="mt-1.5" /></div>
          {!preview && (
            <>
              <div className="rounded-lg border border-dashed border-slate-300 p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-600">CSV columns: <b>First line, Second line, Postcode</b>, Access info, Notes</div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={template} data-testid="import-template-btn"><Download className="h-4 w-4 mr-1.5" />Template</Button>
                  <label className={`inline-flex items-center rounded-md bg-brand px-3 h-9 text-sm font-medium text-white cursor-pointer hover:bg-brand-dark ${!clientId ? "opacity-50 pointer-events-none" : ""}`} data-testid="import-file-label">
                    <Upload className="h-4 w-4 mr-1.5" />Upload CSV
                    <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
                  </label>
                </div>
              </div>
              <div><Label>Or paste addresses, one per line</Label>
                <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="import-paste"
                  placeholder={"269 Great Cheetham Street East, Salford, M7 4UF\n12 Bury New Road, Manchester, M8 8FR"} className="mt-1.5 font-mono text-sm" />
                <Button variant="outline" className="mt-2" disabled={busy || !text.trim()} onClick={() => check(rowsFromPaste(text))} data-testid="import-check-btn">
                  {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Check list</Button>
              </div>
            </>
          )}
          {preview && (
            <div data-testid="import-preview">
              <div className="text-sm mb-2">{done ? <><CheckCircle2 className="inline h-4 w-4 text-emerald-600 mr-1" />{preview.created} added to <b>{preview.client}</b></> : <><b>{preview.ok}</b> of {preview.rows.length} ready to add to <b>{preview.client}</b></>}</div>
              <div className="max-h-72 overflow-y-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-2">#</th><th className="p-2">First line</th><th className="p-2">Second line</th><th className="p-2">Postcode</th><th className="p-2">Status</th></tr></thead>
                  <tbody>{preview.rows.map((r) => (
                    <tr key={r.row} className="border-t" data-testid={`import-row-${r.row}`}>
                      <td className="p-2 text-slate-400">{r.row}</td><td className="p-2">{r.line1}</td><td className="p-2">{r.line2}</td><td className="p-2">{r.postcode}</td>
                      <td className={`p-2 font-medium ${BADGE[r.status]}`}>{r.status === "created" ? (r.pinned ? "Added" : <span className="text-amber-700"><AlertTriangle className="inline h-3.5 w-3.5 mr-0.5" />Added — no map pin</span>) : r.status === "ok" ? "Ready" : r.message}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {preview && !done && <Button variant="ghost" onClick={() => setPreview(null)} data-testid="import-back-btn">Back</Button>}
          {preview && !done && <Button className="bg-brand hover:bg-brand-dark" disabled={busy || !preview.ok} onClick={doImport} data-testid="import-confirm-btn">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Import {preview.ok} properties</Button>}
          {done && <Button onClick={() => { reset(); onOpenChange(false); }} data-testid="import-close-btn">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
